<template>
  <div>
    <refonte-modal
      v-bind:show="props.show"
      class="!max-w-[1200px]"
      v-on:close="openModalAlertClose">
      <template v-slot:modalHeader>
        <h1>{{ formation.titre }}</h1>
        <div class="item-file-content-title-text-bottom">
          Questionnaire de satisfaction
        </div>
      </template>

      <template v-slot:modalBody>
        <template v-if="loading">
          <spinner
            sizes="w-20 h-20"
            thickness="border-8" />
        </template>

        <template v-else>
          <div
            v-if="completed"
            class="p-4">
            <message-box
              v-bind:source="'Merci d\'avoir répondu au questionnaire, votre réponse a bien été prise en compte.'"
              border_color="w-full border-yellow-500"
              font_style="text-lg" />
          </div>
          <template v-else>
            <div
              class="flex w-full flex-wrap space-x-4 px-8 py-4 md:space-x-0 md:space-y-4">
              <div class="flex w-full flex-wrap">
                <div class="block w-full">
                  <surveyjs-wrapper
                    ref="surveyWrapper"
                    v-on:complete="complete"
                    v-bind:json="detail" />
                  <div
                    class="mx-10 font-bold italic text-orange-500"
                    v-if="
                      surveyCatcher && surveyCatcher.isLastPage && !completed
                    ">
                    Pour votre information, en soumettant ce questionnaire, vous
                    acceptez que vos réponses soient exploitées par votre
                    employeur pour améliorer les pratiques internes, en
                    conformité avec le RGPD.
                  </div>
                </div>
              </div>
            </div>
          </template>
        </template>
      </template>
      <template v-slot:modalFooter>
        <div
          v-if="surveyCatcher"
          class="inline-flex w-full justify-end space-x-4 text-lg">
          <refonte-button
            v-if="!surveyCatcher.isFirstPage && !completed"
            v-bind:on_click="() => surveyWrapper.prevPage()">
            Précédent
          </refonte-button>
          <refonte-button
            v-if="!surveyCatcher.isLastPage"
            v-bind:on_click="() => surveyWrapper.nextPage()">
            Suivant
          </refonte-button>
          <refonte-button
            v-if="surveyCatcher.isLastPage && !completed"
            v-bind:on_click="() => surveyWrapper.completeLastPage()">
            Terminer
          </refonte-button>
          <refonte-button
            v-else-if="completed"
            v-bind:on_click="() => close()">
            Fermer
          </refonte-button>
        </div>
      </template>
    </refonte-modal>
    <satisfaction-alert-modal-close
      v-bind:show="isModalAlertCloseOpen"
      v-bind:on_close="closeModalAlertClose"
      v-bind:on_submit="close" />
  </div>
</template>

<script setup lang="ts">
  import RefonteModal from '@/components/refonte/modals/refonte-modal.vue';
  import { onMounted, ref, watchEffect } from 'vue';
  import SatisfactionAlertModalClose from '@/views/_espace/_refonte/gestion/_satisfaction/satisfaction-alert-modal-close.vue';
  import { API } from '@/http-common';
  import SurveyjsWrapper from '@/components/utils/surveyjs-wrapper.vue';
  import RefonteButton from '@/components/refonte/buttons/refonte-button.vue';
  import { Formation } from '@/types/participant/formations-types';
  import MessageBox from '@/components/utils/message-box.vue';

  const props = withDefaults(
    defineProps<{
      show: boolean;
      on_close: Function;
      formation: Formation;
      on_complete?: Function;
    }>(),
    {}
  );
  const detail = ref(null);
  const loading = ref<boolean>(false);
  const isModalAlertCloseOpen = ref<boolean>(false);
  const completed = ref<boolean>(false);
  const surveyCatcher = ref(null);
  const surveyWrapper = ref(null);
  const participantType = ref('');
  const participantId = ref<string | number | null>(null);

  const computeParticipantIdentifiers = () => {
    if (props.formation.isSousProduitParcours) {
      participantType.value = 'sessionParticipantId';
    } else {
      const typeKeyMap = new Map<number, string>([
        [8, 'cursusInscriptionId'],
        [2, 'elearningParticipantId']
      ]);
      const participantKey =
        typeKeyMap.get(props.formation.type.id) ?? 'sessionParticipantId';
      participantType.value = participantKey;
    }

    participantId.value =
      props.formation[participantType.value] ?? props.formation.id;
  };

  onMounted(() => {
    computeParticipantIdentifiers();
    fetchDetail();
  });

  watchEffect(() => {
    if (surveyWrapper.value) {
      surveyCatcher.value = surveyWrapper.value.survey;
    } else {
      // not mounted yet, or the element was unmounted (e.g. by v-if)
    }
  });
  const close = () => {
    detail.value = null;
    props.on_close();
  };
  const openModalAlertClose = () => {
    isModalAlertCloseOpen.value = true;
  };
  const closeModalAlertClose = () => {
    isModalAlertCloseOpen.value = false;
  };

  const fetchDetail = async () => {
    loading.value = true;
    const isSousProduitParcours = props.formation.isSousProduitParcours;
    const parcoursProduitIdParam = isSousProduitParcours
      ? `&parcoursProduitId=${props.formation.produitId}`
      : '';
    const response = await API.get(
      `participant/satisfaction?${participantType.value}=${participantId.value}${parcoursProduitIdParam}`
    );
    if (response && response.data) {
      detail.value = response.data;
    }

    loading.value = false;
  };

  const complete = async () => {
    loading.value = true;

    const data = surveyCatcher.value?.data || {};
    const isSousProduitParcours = props.formation.isSousProduitParcours;
    const parcoursProduitIdParam = isSousProduitParcours
      ? `&parcoursProduitId=${props.formation.produitId}`
      : '';
    const response = await API.post(
      `participant/satisfaction?${participantType.value}=${participantId.value}${parcoursProduitIdParam}`,
      data
    );
    if (response && response.data) {
      completed.value = true;
      if (props.on_complete) {
        props.on_complete();
      }
    }
    loading.value = false;
  };
</script>

<style lang="scss" scoped></style>
